/**
 * Descarga del PDF de una versión de artefacto (OT-0027 Fase 5).
 *
 * Tres vistas ofrecen el mismo botón — la fila de la fase en el tab
 * Oficial, el visor y la vista previa del borrador. La lógica (estado de
 * carga, llamada, disparo del download, toast de error localizado) vive
 * aquí una sola vez en lugar de repetirse en cada una.
 */

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../api/client'
import { downloadArtifactVersionPdf } from '../api/artifacts'
import { triggerBrowserDownload } from '../api/download'
import { useAccessToken } from '../auth/useAccessToken'
import { pushToast } from '../stores/toastStore'

interface DownloadArgs {
  artifactId: string
  versionId: string
  /** Nombre de respaldo si el backend no mandó `Content-Disposition`. */
  fallbackFilename: string
}

export function useArtifactPdfDownload(): {
  download: (args: DownloadArgs) => Promise<void>
  isDownloading: boolean
} {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const [isDownloading, setIsDownloading] = useState(false)

  const download = useCallback(
    async ({ artifactId, versionId, fallbackFilename }: DownloadArgs) => {
      if (!accessToken || isDownloading) return
      setIsDownloading(true)
      try {
        const { blob, filename } = await downloadArtifactVersionPdf(
          accessToken,
          artifactId,
          versionId,
        )
        triggerBrowserDownload(blob, filename ?? fallbackFilename)
      } catch (err) {
        // El backend ya manda el mensaje localizado; el catálogo `errors`
        // es el respaldo cuando el body no trae código reconocible.
        const code = err instanceof ApiError ? err.code : null
        pushToast({
          kind: 'critical',
          message: code
            ? t(`errors:${code}` as 'errors:UNKNOWN', {
                defaultValue: t('projects:official.download_failed'),
              })
            : t('projects:official.download_failed'),
          duration: 6000,
        })
      } finally {
        setIsDownloading(false)
      }
    },
    [accessToken, isDownloading, t],
  )

  return { download, isDownloading }
}
